import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import {
  ListPageShell, ListPageHeader, ListSearchBar, ListFilterSelect,
} from '../components/ui/ListPageChrome';

const TYPE_OPTIONS = ['Machine', 'Labour', 'Other'];

export default function ResourceMaster() {
  const navigate = useNavigate();
  const [rows, setRows]       = useState([]);
  const [search, setSearch]   = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage]         = useState(1);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      let q = '/resources?';
      if (search) q += `search=${encodeURIComponent(search)}&`;
      if (typeFilter) q += `type=${typeFilter}&`;
      const data = await api.get(q);
      setRows(Array.isArray(data) ? data : data?.rows || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [search, typeFilter]);

  useEffect(() => { load(); }, [load]);

  // Reset to page 1 when filters or pageSize change
  useEffect(() => { setPage(1); }, [search, typeFilter, pageSize]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const paged = rows.slice((page - 1) * pageSize, page * pageSize);

  // Build page number buttons (show max 5 around current)
  const pageNumbers = [];
  const maxBtns = 5;
  let startP = Math.max(1, page - Math.floor(maxBtns / 2));
  let endP = Math.min(totalPages, startP + maxBtns - 1);
  if (endP - startP + 1 < maxBtns) startP = Math.max(1, endP - maxBtns + 1);
  for (let i = startP; i <= endP; i++) pageNumbers.push(i);

  return (
    <ListPageShell>
      <ListPageHeader
        title="Resource Master Data"
        subtitle={`${rows.length} record${rows.length !== 1 ? 's' : ''}`}
        action={
          <button type="button" onClick={() => navigate('/resource/new')} className="btn-primary">
            + Add Resource
          </button>
        }
      />

      <div className="flex gap-3 items-center flex-wrap">
        <ListSearchBar value={search} onChange={setSearch} placeholder="Search resources…" />
        <ListFilterSelect value={typeFilter} onChange={setTypeFilter}>
          <option value="">All Types</option>
          {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
        </ListFilterSelect>
        <div className="flex items-center gap-2 shrink-0">
          <label className="text-sm text-sap-textLight">Show</label>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="border border-sap-border rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sap-blue/20 focus:border-sap-blue"
          >
            {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <label className="text-sm text-sap-textLight">entries</label>
        </div>
      </div>

      <div className="data-table-wrap">
        <table className="w-full text-sm">
          <thead>
            <tr className="table-head">
              <th className="text-left px-4 py-3 font-semibold text-2xs uppercase tracking-wider">Resource No</th>
              <th className="text-left px-4 py-3 font-semibold text-2xs uppercase tracking-wider">Description</th>
              <th className="text-left px-4 py-3 font-semibold text-2xs uppercase tracking-wider">Type</th>
              <th className="text-right px-4 py-3 font-semibold text-2xs uppercase tracking-wider">Std Cost</th>
              <th className="text-left px-4 py-3 font-semibold text-2xs uppercase tracking-wider">Vendor</th>
              <th className="text-left px-4 py-3 font-semibold text-2xs uppercase tracking-wider">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-sap-textLight">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-sap-textLight">No resources found</td></tr>
            ) : paged.map((r) => (
              <tr
                key={r.resource_id}
                onClick={() => navigate(`/resource/${r.resource_id}`)}
                className="table-row even:bg-gray-50/50"
              >
                <td className="px-4 py-3 font-semibold text-sap-blue">{r.resource_no}</td>
                <td className="px-4 py-3">{r.description}</td>
                <td className="px-4 py-3">
                  <span className={`px-2.5 py-0.5 rounded-lg text-xs font-semibold
                    ${r.resource_type === 'Machine' ? 'bg-blue-100 text-blue-700' :
                      r.resource_type === 'Labour' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                    {r.resource_type}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-semibold">₹{Number(r.std_cost || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                <td className="px-4 py-3 text-sap-textLight">{r.vendor_name || '—'}</td>
                <td className="px-4 py-3">
                  {r.status === 'Active' ? (
                    <span className="badge-active">{r.status}</span>
                  ) : (
                    <span className="px-2.5 py-0.5 rounded-lg text-xs font-semibold bg-red-100 text-red-600">{r.status}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rows.length > 0 && (
        <div className="flex items-center justify-between text-xs text-sap-textLight">
          <span>
            Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, rows.length)} of {rows.length} entries
          </span>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => setPage(1)} disabled={page === 1}
              className="px-2.5 py-1 rounded-lg border border-sap-border disabled:opacity-40 hover:bg-sap-blue/5 transition-colors">‹‹</button>
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
              className="px-2.5 py-1 rounded-lg border border-sap-border disabled:opacity-40 hover:bg-sap-blue/5 transition-colors">‹</button>
            {pageNumbers.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setPage(n)}
                className={`px-2.5 py-1 rounded-lg border text-xs font-medium transition-colors
                  ${n === page ? 'bg-sap-blue text-white border-sap-blue' : 'border-sap-border hover:bg-sap-blue/5'}`}
              >
                {n}
              </button>
            ))}
            <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-2.5 py-1 rounded-lg border border-sap-border disabled:opacity-40 hover:bg-sap-blue/5 transition-colors">›</button>
            <button type="button" onClick={() => setPage(totalPages)} disabled={page === totalPages}
              className="px-2.5 py-1 rounded-lg border border-sap-border disabled:opacity-40 hover:bg-sap-blue/5 transition-colors">››</button>
          </div>
        </div>
      )}
    </ListPageShell>
  );
}
